/**
 * Draft → Supabase commit helpers.
 *
 * Each function mirrors the existing manual-flow write path exactly, so a
 * brain-authored draft behaves identically to a coach building it by hand.
 *
 * Reference save paths (for parity):
 *  - Session + drills: SessionDetail.tsx (AI drill generation), TrainingCalendar.tsx
 *  - Drill: DrillAuthor.tsx
 *  - Principle: PhilosophyHub.tsx
 *  - Dev note: VideoAnalysis.tsx (only current writer)
 */
import { supabase } from '@4cm/core/supabase';
import {
  PHASE_COVERAGE_KEYS,
  inferPhaseOfPlay,
  normalisePhaseCoverage,
  type PhaseCoverage,
  type PhaseOfPlay,
} from '@4cm/core/phaseOfPlay';
import {
  patchReview,
  saveCoachPlayerGrade,
  upsertDrillGrade,
} from '@4cm/core/sessionReview/persistence';
import { DEMO_COACH_ID } from './brainTools';
import { createMemory, type MemoryKind, type MemoryScope } from './brainMemory';

/** Check-or-insert for a public.users row. Cheap — one select, then
 *  insert only on miss. Used defensively before any FK=>users write so
 *  the commit works even if App hasn't run ensureCoachUser yet. */
async function ensurePublicUserRow(userId: string): Promise<void> {
  // users.email is NOT NULL, so fall back to a synthetic placeholder.
  let email = `coach+${userId.slice(0, 8)}@4cm.local`;
  let name = 'Coach';
  try {
    const { data: auth } = await supabase.auth.getSession();
    const au = auth.session?.user;
    if (au && au.id === userId) {
      email = au.email ?? email;
      const meta = (au.user_metadata ?? {}) as Record<string, unknown>;
      name = (meta.full_name as string) || (meta.name as string) || (au.email?.split('@')[0] ?? 'Coach');
    }
  } catch { /* ignore */ }
  // Upsert instead of SELECT-then-INSERT: in demo mode (no auth.uid()),
  // the users_read_* policies filter out rows so the SELECT always
  // misses and the subsequent INSERT hits users_pkey. ignoreDuplicates
  // makes this idempotent regardless of row visibility.
  const { error } = await supabase.from('users').upsert(
    { id: userId, email, name, role: 'COACH' },
    { onConflict: 'id', ignoreDuplicates: true },
  );
  if (error) {
    // eslint-disable-next-line no-console
    console.warn('[ensurePublicUserRow] upsert failed', error.message ?? error);
  }
}

// ---------------------------------------------------------------------------
// Shared types
// ---------------------------------------------------------------------------

export interface CommitResult {
  ok: boolean;
  id?: string;
  error?: string;
}

// ---------------------------------------------------------------------------
// 1. Session plan
// ---------------------------------------------------------------------------

export interface SessionPlanDraft {
  team_id?: string;
  date: string;
  title: string;
  duration_mins: number;
  objectives?: string[];
  psych_social_intent: string;
  phase_coverage?: Partial<PhaseCoverage>;
  /** Legacy secondary axis. Phase-of-play is primary for web2 sessions. */
  corner_coverage?: {
    technical_tactical: number;
    physical: number;
    psychological: number;
    social: number;
  };
  curriculum_week?: number;
  drills: Array<{
    name: string;
    description: string;
    drill_type?: string;
    primary_phase?: PhaseOfPlay | string | null;
    sub_themes?: string[];
    duration_mins: number;
    corner_ids?: string[];
    sub_corner_ids?: string[];
    min_players?: number;
    max_players?: number;
    space_required?: string;
    equipment?: string[];
    coaching_points?: string[];
    progressions?: string[];
    /** The in-rep decision for a TECHNICAL drill (prompt v1.2). Carried on
     *  the draft for the decision-under-execution validator + display; not
     *  persisted by commitSessionPlan (no drills column yet). */
    decision_under_execution?: string;
  }>;
}

const DEFAULT_CORNER_COVERAGE = {
  technical_tactical: 40,
  physical: 20,
  psychological: 20,
  social: 20,
};

function toPhaseOfPlay(value: unknown): PhaseOfPlay | null {
  if (typeof value !== 'string') return null;
  return (PHASE_COVERAGE_KEYS as readonly string[]).includes(value) ? value as PhaseOfPlay : null;
}

function numericCoverageValue(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) ? Math.max(0, value) : 0;
}

function coercePhaseCoverage(value: Partial<PhaseCoverage> | undefined): PhaseCoverage | null {
  if (!value) return null;
  const coverage: PhaseCoverage = {
    attacking: numericCoverageValue(value.attacking),
    defending: numericCoverageValue(value.defending),
    transitions: numericCoverageValue(value.transitions),
    individual: numericCoverageValue(value.individual),
  };
  const total = PHASE_COVERAGE_KEYS.reduce((sum, key) => sum + coverage[key], 0);
  return total > 0 ? normalisePhaseCoverage(coverage) : null;
}

function phaseCoverageFromDraft(draft: SessionPlanDraft): PhaseCoverage {
  const explicit = coercePhaseCoverage(draft.phase_coverage);
  if (explicit) return explicit;

  const drillTotals: PhaseCoverage = { attacking: 0, defending: 0, transitions: 0, individual: 0 };
  for (const drill of draft.drills ?? []) {
    const text = [
      drill.name,
      drill.description,
      drill.drill_type,
      ...(drill.sub_themes ?? []),
    ].filter(Boolean).join(' ');
    const phase = toPhaseOfPlay(drill.primary_phase) ?? inferPhaseOfPlay(text);
    if (!phase) continue;
    drillTotals[phase] += numericCoverageValue(drill.duration_mins) || 1;
  }
  const drillTotal = PHASE_COVERAGE_KEYS.reduce((sum, key) => sum + drillTotals[key], 0);
  if (drillTotal > 0) return normalisePhaseCoverage(drillTotals, 0);

  const sessionPhase = inferPhaseOfPlay([
    draft.title,
    draft.psych_social_intent,
    ...(draft.objectives ?? []),
  ].join(' ')) ?? 'individual';
  const fallback: PhaseCoverage = { attacking: 10, defending: 10, transitions: 10, individual: 10 };
  fallback[sessionPhase] = 70;
  return fallback;
}

export async function commitSessionPlan(
  draft: SessionPlanDraft,
  opts: { teamId: string | null; coachId?: string },
): Promise<CommitResult> {
  const teamId = draft.team_id ?? opts.teamId;
  if (!teamId) return { ok: false, error: 'no active team' };
  const coachId = opts.coachId ?? DEMO_COACH_ID;

  // Defensive: sessions.coach_id is FK to public.users(id). If the auth
  // user hasn't been mirrored yet (fresh login before App ran
  // ensureCoachUser), the FK fails. Check + seed a minimal row right
  // here so the commit self-heals in the middle of a Brain flow.
  await ensurePublicUserRow(coachId);
  const phaseCoverage = phaseCoverageFromDraft(draft);
  const cornerCoverage = draft.corner_coverage ?? DEFAULT_CORNER_COVERAGE;

  // 1. Insert session
  const { data: session, error: sessErr } = await supabase.from('sessions').insert({
    team_id: teamId,
    coach_id: coachId,
    date: draft.date,
    title: draft.title,
    duration_mins: draft.duration_mins,
    objectives: draft.objectives ?? [],
    corner_coverage: cornerCoverage,
    phase_coverage: phaseCoverage,
    psych_social_intent: draft.psych_social_intent,
    curriculum_week: draft.curriculum_week ?? null,
    status: 'DRILLS_GENERATED',
  }).select('id').single();
  if (sessErr || !session) {
    const hint = sessErr?.message?.includes('coach_id_fkey')
      ? ' (this coach id is missing from the users table — sign out/in, or ask me to seed it)'
      : '';
    return { ok: false, error: (sessErr?.message ?? 'session insert failed') + hint };
  }

  // 2. Insert drills + session_drills join
  if (draft.drills?.length) {
    const drillRows = draft.drills.map(d => ({
      org_id: null,
      name: d.name,
      description: d.description,
      corner_ids: d.corner_ids ?? [],
      sub_corner_ids: d.sub_corner_ids ?? [],
      duration_mins: d.duration_mins,
      is_custom: false,
      source_type: 'AI_GENERATED' as const,
      drill_type: d.drill_type ?? null,
      primary_phase: toPhaseOfPlay(d.primary_phase) ?? inferPhaseOfPlay([
        d.name,
        d.description,
        d.drill_type,
        ...(d.sub_themes ?? []),
      ].filter(Boolean).join(' ')),
      sub_themes: d.sub_themes ?? [],
      min_players: d.min_players ?? null,
      max_players: d.max_players ?? null,
      space_required: d.space_required ?? null,
      equipment: d.equipment ?? [],
      coaching_points: d.coaching_points ?? [],
      progressions: d.progressions ?? [],
    }));
    const { data: drills, error: drillErr } = await supabase.from('drills')
      .insert(drillRows).select('id');
    if (drillErr || !drills) return { ok: false, error: drillErr?.message ?? 'drills insert failed' };

    const joinRows = drills.map((row, i) => ({
      session_id: session.id,
      drill_id: row.id,
      sort_order: i,
    }));
    const { error: joinErr } = await supabase.from('session_drills').insert(joinRows);
    if (joinErr) return { ok: false, error: joinErr.message };
  }

  return { ok: true, id: session.id };
}

// ---------------------------------------------------------------------------
// 2. Drill (standalone, library entry)
// ---------------------------------------------------------------------------

export interface DrillDraft {
  name: string;
  description: string;
  drill_type?: string;
  duration_mins: number;
  corner_ids: string[];
  sub_corner_ids?: string[];
  primary_corner_id?: string;
  min_players?: number;
  max_players?: number;
  space_required?: string;
  equipment?: string[];
  coaching_points?: string[];
  progressions?: string[];
  regressions?: string[];
  difficulty?: string;
  phase_objective?: string;
  four_cm_connection?: string;
  curriculum_phase?: string[];
  /** Visual layout — pitch + entities + cones + arrows + zones at
   *  normalised positions. When present, the canvas preview renders.
   *  When absent, only the drill metadata renders. Brain emits this by
   *  setting positions on a small reference layout (see DRILL_SPEC_GUIDANCE
   *  in brainPrompt.ts). Persisted as `drills.drill_spec` jsonb (migration
   *  00056). Typed as unknown here so brain stays decoupled from the full
   *  DrillSpec type tree in @4cm/core/drills/types — the renderer validates. */
  drill_spec?: unknown;
}

const DRILL_DIFFICULTY = ['FOUNDATION', 'INTERMEDIATE', 'ADVANCED'] as const;

export async function commitDrill(draft: DrillDraft): Promise<CommitResult> {
  // Validate required.
  if (!draft.name?.trim()) return { ok: false, error: 'Drill name is empty.' };
  if (!draft.description?.trim()) return { ok: false, error: 'Drill description is empty.' };
  if (!(draft.duration_mins > 0)) return { ok: false, error: 'Drill duration must be > 0 minutes.' };
  // Normalise enum — accept lowercase / stray whitespace.
  const difficultyRaw = String(draft.difficulty ?? 'INTERMEDIATE').trim().toUpperCase();
  const difficulty = (DRILL_DIFFICULTY as readonly string[]).includes(difficultyRaw) ? difficultyRaw : 'INTERMEDIATE';
  const row = {
    org_id: null,
    name: draft.name,
    description: draft.description,
    corner_ids: draft.corner_ids ?? [],
    sub_corner_ids: draft.sub_corner_ids ?? [],
    primary_corner_id: draft.primary_corner_id ?? draft.corner_ids?.[0] ?? null,
    duration_mins: draft.duration_mins,
    typical_duration_mins: draft.duration_mins,
    is_custom: true,
    source_type: 'CLUB' as const,
    curriculum_phase: draft.curriculum_phase ?? [],
    difficulty: difficulty as any,
    drill_type: draft.drill_type ?? null,
    min_players: draft.min_players ?? null,
    max_players: draft.max_players ?? null,
    space_required: draft.space_required ?? null,
    equipment: draft.equipment ?? [],
    coaching_points: (draft.coaching_points ?? []).filter(s => s.trim().length > 0),
    progressions: (draft.progressions ?? []).filter(s => s.trim().length > 0),
    regressions: (draft.regressions ?? []).filter(s => s.trim().length > 0),
    phase_objective: draft.phase_objective ?? null,
    four_cm_connection: draft.four_cm_connection ?? null,
    // drill_spec is the canonical visual layout — `drills.drill_spec` jsonb
    // column added by migration 00056. Brain's draft_drill tool now emits
    // this shape; the legacy DrillSpec-less path still works (the column
    // is nullable, the canvas preview falls through to metadata-only).
    drill_spec: draft.drill_spec ?? null,
  };
  const { data, error } = await supabase.from('drills').insert(row).select('id').single();
  if (error || !data) return { ok: false, error: error?.message ?? 'drill insert failed' };
  return { ok: true, id: data.id };
}

// ---------------------------------------------------------------------------
// 3. Development entry (coach note against a player + corner)
// ---------------------------------------------------------------------------

export interface DevNoteDraft {
  player_id: string;
  corner_id: string;          // UUID — passed through from get_ratings
  sub_corner_id?: string;
  period: string;             // WEEK | MONTH | SEASON
  coach_notes: string;
  self_assessment?: string;
  evidence_clip_url?: string;
  video_tag_id?: string;
}

export async function commitDevNote(draft: DevNoteDraft): Promise<CommitResult> {
  const row = {
    player_id: draft.player_id,
    corner_id: draft.corner_id,
    sub_corner_id: draft.sub_corner_id ?? null,
    period: draft.period as any,
    self_assessment: draft.self_assessment ?? '',
    coach_notes: draft.coach_notes,
    evidence_clip_url: draft.evidence_clip_url ?? null,
    video_tag_id: draft.video_tag_id ?? null,
  };
  const { data, error } = await supabase.from('development_entries')
    .insert(row).select('id').single();
  if (error || !data) return { ok: false, error: error?.message ?? 'dev note insert failed' };
  return { ok: true, id: data.id };
}

// ---------------------------------------------------------------------------
// 4. Principle (coaching philosophy line)
// ---------------------------------------------------------------------------

export interface PrincipleDraft {
  team_id?: string;
  type: string;                // ATTACK | DEFEND | TRANSITION | RESTART
  text: string;
  corner_ids: string[];
  phase?: string;
}

const PRINCIPLE_TYPES = ['ATTACK', 'DEFEND', 'TRANSITION', 'RESTART'] as const;

const VALID_CORNER_IDS = new Set([
  'corner-technical',
  'corner-physical',
  'corner-psych',
  'corner-social',
]);

export async function commitPrinciple(
  draft: PrincipleDraft,
  opts: { teamId: string | null },
): Promise<CommitResult> {
  const teamId = draft.team_id ?? opts.teamId;
  if (!teamId) return { ok: false, error: "No team yet — finish Team Setup first (step 1), then approve this principle." };
  // Normalise enum — Claude sometimes emits lowercase or mixed case.
  const type = String(draft.type ?? '').trim().toUpperCase();
  if (!(PRINCIPLE_TYPES as readonly string[]).includes(type)) {
    return { ok: false, error: `Invalid principle type "${draft.type}". Must be one of ${PRINCIPLE_TYPES.join(' | ')}.` };
  }
  if (!draft.text?.trim()) return { ok: false, error: 'Principle text is empty.' };
  // Filter any bogus/unknown ids the model may have hallucinated, and
  // enforce the 4CM rule: at least one corner tagged per principle.
  const corner_ids = (draft.corner_ids ?? []).filter((c) => VALID_CORNER_IDS.has(c));
  if (corner_ids.length === 0) {
    return {
      ok: false,
      error: 'Principle must tag at least one corner (corner-technical, corner-physical, corner-psych, or corner-social). Brain left corner_ids empty — ask it to re-classify.',
    };
  }
  const { data, error } = await supabase.from('principles').insert({
    team_id: teamId,
    type: type as any,
    text: draft.text,
    corner_ids,
    phase: draft.phase ?? null,
  }).select('id').single();
  if (error || !data) return { ok: false, error: error?.message ?? 'principle insert failed' };
  return { ok: true, id: data.id };
}

// ---------------------------------------------------------------------------
// 5. Memory (brain_memories)
// ---------------------------------------------------------------------------

export interface MemoryDraft {
  scope: MemoryScope;
  entity_id?: string | null;
  kind: MemoryKind;
  fact: string;
  tags?: string[];
}

export async function commitMemory(
  draft: MemoryDraft,
  opts: { coachId: string; teamId: string | null },
): Promise<CommitResult> {
  const row = await createMemory({
    coach_id: opts.coachId,
    team_id: opts.teamId,
    scope: draft.scope,
    entity_id: draft.entity_id ?? null,
    kind: draft.kind,
    fact: draft.fact,
    tags: draft.tags ?? [],
    evidence: { source: 'coach_approved_draft' },
    confidence: 0.75,
  });
  if (!row) return { ok: false, error: 'memory insert failed' };
  return { ok: true, id: row.id };
}

// ---------------------------------------------------------------------------
// 6. Drill grade (post-session review)
// ---------------------------------------------------------------------------

export interface DrillGradeDraft {
  session_review_id: string;
  session_drill_id: string;
  sentiment?: 'worked' | 'mixed' | 'struggled' | null;
  grade?: number | null;
  coach_notes?: string | null;
  reasoning?: string;    // Brain-supplied rationale; not persisted
}

export async function commitDrillGrade(draft: DrillGradeDraft): Promise<CommitResult> {
  if (!draft.session_review_id) return { ok: false, error: 'session_review_id required' };
  if (!draft.session_drill_id) return { ok: false, error: 'session_drill_id required' };
  const ok = await upsertDrillGrade(draft.session_review_id, draft.session_drill_id, {
    sentiment: draft.sentiment ?? null,
    grade: draft.grade ?? null,
    coach_notes: draft.coach_notes ?? null,
  });
  return ok ? { ok: true } : { ok: false, error: 'drill grade write failed' };
}

// ---------------------------------------------------------------------------
// 7. Player grade (per-corner session rating — writes to `ratings`)
// ---------------------------------------------------------------------------

export interface PlayerGradeDraft {
  session_id: string;
  player_id: string;
  /** { cornerUuid: score 0-100 } */
  corner_scores: Record<string, number>;
  reasoning?: string;
}

export async function commitPlayerGrade(
  draft: PlayerGradeDraft,
  opts: { coachId: string },
): Promise<CommitResult> {
  if (!draft.session_id) return { ok: false, error: 'session_id required' };
  if (!draft.player_id) return { ok: false, error: 'player_id required' };
  const entries = Object.entries(draft.corner_scores ?? {});
  if (entries.length === 0) return { ok: false, error: 'no corner scores' };
  for (const [cornerId, score] of entries) {
    const ok = await saveCoachPlayerGrade({
      coachId: opts.coachId,
      sessionId: draft.session_id,
      grade: {
        ratee_id: draft.player_id,
        corner_id: cornerId,
        score: Math.max(0, Math.min(100, Math.round(score))),
      },
    });
    if (!ok) return { ok: false, error: `failed to save grade on corner ${cornerId}` };
  }
  return { ok: true };
}

// ---------------------------------------------------------------------------
// 8.0 Curriculum plan (full season)
// ---------------------------------------------------------------------------

export interface CurriculumDraft {
  team_id?: string;
  season_overview: string;
  total_weeks: number;
  /** Coach-facing summary of the planned weeks (prompt v1.1+). Optional. */
  what_to_expect?: string;
  phase_plans: Array<{
    phase: string;
    weeks: number;
    theme: string;
    /** Why this theme now (prompt v1.1+). Optional. */
    rationale?: string;
    corner_emphasis: {
      technical_tactical: number;
      physical: number;
      psychological: number;
      social: number;
    };
    weekly_topics: Array<{
      week_number: number;
      date_start: string;
      topic: string;
      primary_corner: string;
      sub_corners?: string[];
      session_count: number;
      principle_connection?: string;
      intensity?: string;
    }>;
  }>;
  season_start?: string;
  season_end?: string;
}

export async function commitCurriculum(
  draft: CurriculumDraft,
  opts: { teamId: string | null; coachId?: string },
): Promise<CommitResult> {
  const teamId = draft.team_id ?? opts.teamId;
  if (!teamId) return { ok: false, error: 'no active team — finish Team Setup first' };
  const coachId = opts.coachId ?? DEMO_COACH_ID;

  // Same FK hazard as sessions/principles — if App hasn't run
  // ensureCoachUser yet, the coach_id FK fails. Seed a public.users
  // row on demand.
  await ensurePublicUserRow(coachId);

  // Pull season dates from the team if not on the draft — the planner
  // tool doesn't always supply them.
  let seasonStart = draft.season_start;
  let seasonEnd = draft.season_end;
  if (!seasonStart || !seasonEnd) {
    const { data: team } = await supabase.from('teams')
      .select('season_start, season_end')
      .eq('id', teamId).maybeSingle();
    seasonStart = seasonStart ?? team?.season_start;
    seasonEnd = seasonEnd ?? team?.season_end;
  }

  // Deactivate any existing active plan; one active plan per team.
  await supabase.from('curriculum_plans')
    .update({ is_active: false })
    .eq('team_id', teamId)
    .eq('is_active', true);

  // Reconcile total_weeks to the topics actually listed — the model's number
  // can drift from Σ weekly_topics on long seasons (Brain eval finding).
  const weekCount = (draft.phase_plans ?? []).reduce(
    (n, p) => n + (p.weekly_topics?.length ?? 0),
    0,
  );
  const { data, error } = await supabase.from('curriculum_plans').insert({
    team_id: teamId,
    coach_id: coachId,
    season_start: seasonStart ?? null,
    season_end: seasonEnd ?? null,
    plan_data: {
      season_overview: draft.season_overview,
      total_weeks: weekCount || draft.total_weeks,
      what_to_expect: draft.what_to_expect ?? null,
      phase_plans: draft.phase_plans,
    },
    is_active: true,
  }).select('id').single();
  if (error || !data) return { ok: false, error: error?.message ?? 'curriculum insert failed' };
  return { ok: true, id: data.id };
}

// ---------------------------------------------------------------------------
// 9. Player multidisciplinary review
// ---------------------------------------------------------------------------

export interface PlayerReviewDraft {
  player_id: string;
  template_variant: string;
  review_number?: number;
  period_start: string;
  period_end: string;
  focus_areas: Record<string, string[]>;
  parent_summary?: string;
}

export async function commitPlayerReview(
  draft: PlayerReviewDraft,
  opts: { coachId: string; teamId: string | null },
): Promise<CommitResult> {
  if (!draft.player_id) return { ok: false, error: 'player_id required' };
  if (!draft.template_variant) return { ok: false, error: 'template_variant required' };

  await ensurePublicUserRow(opts.coachId);

  // Derive next review number if not supplied.
  let reviewNumber = draft.review_number ?? 0;
  if (!reviewNumber) {
    const { data } = await supabase.from('development_plans')
      .select('review_number')
      .eq('player_id', draft.player_id)
      .is('deleted_at', null)
      .order('review_number', { ascending: false, nullsFirst: false })
      .limit(1);
    reviewNumber = ((data?.[0]?.review_number as number | null) ?? 0) + 1;
  }

  // Mirror focus_areas into corner_priorities for the legacy IDP view.
  const fa = draft.focus_areas ?? {};
  const cornerPriorities: Record<string, string[]> = {
    'corner-technical': fa.technical ?? [],
    'corner-physical':  fa.physical ?? [],
    'corner-psych':     fa.psycho_social ?? [],
    'corner-social':    [],
  };

  const { data, error } = await supabase.from('development_plans').insert({
    player_id: draft.player_id,
    coach_id: opts.coachId,
    period_start: draft.period_start,
    period_end: draft.period_end,
    plan_type: 'SHORT_TERM',
    review_number: reviewNumber,
    template_variant: draft.template_variant,
    focus_areas: fa,
    parent_summary: draft.parent_summary ?? null,
    corner_priorities: cornerPriorities,
    milestones: [],
  }).select('id').single();
  if (error || !data) return { ok: false, error: error?.message ?? 'player review insert failed' };
  return { ok: true, id: data.id };
}

// ---------------------------------------------------------------------------
// 8. Session review text (notes_working / issues / next_tweaks / narrative)
// ---------------------------------------------------------------------------

export interface SessionReviewDraft {
  session_review_id: string;
  notes_working?: string;
  notes_issues?: string;
  next_session_tweaks?: string;
  ai_narrative?: string;
}

export async function commitSessionReview(draft: SessionReviewDraft): Promise<CommitResult> {
  if (!draft.session_review_id) return { ok: false, error: 'session_review_id required' };
  const ok = await patchReview(draft.session_review_id, {
    notes_working: draft.notes_working,
    notes_issues: draft.notes_issues,
    next_session_tweaks: draft.next_session_tweaks,
    ai_narrative: draft.ai_narrative,
  });
  return ok ? { ok: true } : { ok: false, error: 'session review write failed' };
}

// ---------------------------------------------------------------------------
// Dispatcher — keyed by draft.type (mirrors tool names)
// ---------------------------------------------------------------------------

export async function commitDraft(
  type: string,
  payload: unknown,
  opts: { teamId: string | null; coachId?: string },
): Promise<CommitResult> {
  try {
    switch (type) {
      case 'draft_session_plan':
        return await commitSessionPlan(payload as SessionPlanDraft, opts);
      case 'draft_drill':
        return await commitDrill(payload as DrillDraft);
      case 'draft_development_note':
        return await commitDevNote(payload as DevNoteDraft);
      case 'draft_principle':
        return await commitPrinciple(payload as PrincipleDraft, { teamId: opts.teamId });
      case 'draft_memory':
        return await commitMemory(payload as MemoryDraft, {
          coachId: opts.coachId ?? DEMO_COACH_ID,
          teamId: opts.teamId,
        });
      case 'draft_drill_grade':
        return await commitDrillGrade(payload as DrillGradeDraft);
      case 'draft_player_grade':
        return await commitPlayerGrade(payload as PlayerGradeDraft, {
          coachId: opts.coachId ?? DEMO_COACH_ID,
        });
      case 'draft_session_review':
        return await commitSessionReview(payload as SessionReviewDraft);
      case 'draft_curriculum':
        return await commitCurriculum(payload as CurriculumDraft, opts);
      case 'draft_player_review':
        return await commitPlayerReview(payload as PlayerReviewDraft, {
          coachId: opts.coachId ?? DEMO_COACH_ID,
          teamId: opts.teamId,
        });
      default:
        return { ok: false, error: `unknown draft type: ${type}` };
    }
  } catch (err) {
    return { ok: false, error: err instanceof Error ? err.message : String(err) };
  }
}
